import type { VoucherStatus } from './traveler_view';

const DEAD_VOUCHER_STATUSES: ReadonlySet<string> = new Set<VoucherStatus>([
  'CANCELLED',
  'EXPIRED',
  'DECLINED',
]);

/**
 * The voucher left the game without the product happening: cancelled, expired or
 * declined. What died promises nothing — it neither composes the trip nor holds
 * its fulfillment back. `CONFIRMED` is terminal too, but alive. An unknown status
 * (`null`, or a mirror row not read yet) is never dead: unknown is not cancelled.
 *
 * Takes `string` on purpose: trip-api also feeds it already-mapped status strings
 * read from other surfaces (the approvals "rest of the trip" rows).
 */
export function isDeadVoucherStatus(status: string | null | undefined): boolean {
  return status ? DEAD_VOUCHER_STATUSES.has(status) : false;
}

/**
 * The traveler asked to cancel an ISSUED voucher and the legacy system has not
 * cancelled it yet: the status stays `CONFIRMED` until the source flips to
 * CANCELED (which clears `cancellationRequestedAt`). Issued, but no longer a
 * promise the traveler is counting on.
 */
export function isCancellationRequestedVoucher(
  voucher: Readonly<{
    status: VoucherStatus | null | undefined;
    cancellationRequestedAt?: Date | null | undefined;
  }>,
): boolean {
  return voucher.status === 'CONFIRMED' && voucher.cancellationRequestedAt != null;
}

/** Tag que o legado põe no voucher quando o back office está trocando o voo. */
export const VOUCHER_TAG_REQUESTED_CHANGE = 'REQUESTED_CHANGE' as const;

/** A lista de tags do voucher traz `REQUESTED_CHANGE` (sem distinguir caixa nem espaços). */
export function hasRequestedChangeTag(tags: readonly string[] | null | undefined): boolean {
  const wanted = VOUCHER_TAG_REQUESTED_CHANGE.toLowerCase();
  return (tags ?? []).some((tag) => tag.trim().toLowerCase() === wanted);
}

/** Os modais de voo: o voo comprado no fluxo e o arranjado pela concierge. */
const FLIGHT_MODALS: ReadonlySet<string> = new Set(['FLIGHT', 'CONCIERGE_FLIGHT']);

/**
 * O back office pediu a troca do voo de um voucher EMITIDO e o legado ainda não
 * concluiu: o status segue `CONFIRMED`, mas o viajante não vai voar nele — o voo
 * está sendo trocado. Emitido, porém não é mais a promessa em que ele conta.
 * Só VOO: hotel e carro com a mesma tag seguem como emitidos. `modal` é
 * obrigatório para quem chama não esquecer de passá-lo.
 */
export function isChangeRequestedVoucher(
  voucher: Readonly<{
    modal: string | null | undefined;
    status: VoucherStatus | null | undefined;
    tags?: readonly string[] | null | undefined;
  }>,
): boolean {
  return (
    voucher.status === 'CONFIRMED' &&
    FLIGHT_MODALS.has(voucher.modal?.trim().toUpperCase() ?? '') &&
    hasRequestedChangeTag(voucher.tags)
  );
}

/**
 * O trecho de voo deixou de valer: o atendimento cancelou SÓ esta perna na
 * companhia depois da emissão (a ida de um ida-e-volta vendido num voucher só,
 * #6394). O legado não tem estado por trecho, então a marca é nossa — o
 * customer-api a anexa na leitura. Sem `cancelledAt` (espelho antigo, ou trecho
 * nunca marcado) a perna está ativa: ausência nunca é "cancelado".
 */
export function isCancelledFlightLeg(segment: Readonly<{ cancelledAt?: Date | null }>): boolean {
  return segment.cancelledAt != null;
}

/** Os trechos que ainda vão voar, na ordem do voucher — a régua de toda superfície que
 *  pergunta "qual o próximo voo", "quando a viagem começa" ou "o que vai na carteira". */
export function liveFlightSegments<T extends Readonly<{ cancelledAt?: Date | null }>>(
  segments: readonly T[],
): T[] {
  return segments.filter((segment) => !isCancelledFlightLeg(segment));
}
